<template>
  <main class="d-flex flex-column gap-1 gap-xl-3 container mt-1">
    <div aria-live="polite">
      Status: {{ status }}
      <span v-if="play_steps > 0" class="ms-3">Steps: {{ play_steps }}</span>
    </div>
    <div v-if="game_result" class="alert" :class="game_result === 'won' ? 'alert-success' : 'alert-danger'" role="status">
      {{ game_result === 'won' ? 'The AI won the round!' : 'The AI lost the round.' }}
    </div>
    <div class="d-flex flex-column flex-lg-row gap-3 mt-1">
      <section class="d-flex flex-column row-gap-1">
        <game-table @lost="lost_fn" @won="won_fn" ref="game_table" v-model:current_score="current_score" :table-size="table_size" :paused="training && no_delay" :block-input="block_input" />
        <button @click="clicked" :disabled="block_input" class="btn btn-danger">reset grid</button>
      </section>
      <section class="d-flex score flex-column gap-1">
        <div class="d-flex flex-column">
          <span><span lang="de">Aktueller Score</span>: {{current_score}}</span>
          <span>Max Score: {{max_score}}</span>
          <label for="table_size_input" class="form-label">Table Size</label>
          <input v-model="table_size" type="number" min="4" max="50" id="table_size_input" :disabled="true" class="form-control" aria-describedby="table_size_help">
          <div id="table_size_help" class="form-text">The size of the grid</div>
        </div>
        <div class="d-flex flex-column">
          <div>
            <label for="current_model" class="form-label">Current loaded model</label><br>
            <select id="current_model" class="form-select" v-model="currently_selected_model" :disabled="block_input" aria-describedby="current_model_help">
              <option v-for="model in Object.keys(models)" :value="model">
                {{model}}
              </option>
            </select>
            <div id="current_model_help" class="form-text">The model to load to use</div>
          </div>

          <label for="memory_slots_input" class="form-label">Memory Slots</label>
          <input type="number" min="1" max="100000" id="memory_slots_input" v-model="memory_slots" :disabled="block_input" class="form-control" aria-describedby="memory_slots_help">
          <div id="memory_slots_help" class="form-text">How much memory to give the neural network</div>
          <label for="training_delay_input" class="form-label">Step delay</label>
          <div class="form-check">
            <input type="checkbox" class="form-check-input" id="disable_delay" :disabled="block_input" v-model="no_delay">
            <label for="disable_delay" class="form-check-label">Disable delay</label>
          </div>
          <input type="number" min="1" max="10000" id="training_delay_input" v-model="training_delay" :disabled="block_input || no_delay" class="form-control" aria-describedby="training_delay_help">
          <div id="training_delay_help" class="form-text">How much time between the steps performed by the neural network</div>

          <label for="batch_size_help_input" class="form-label">Batch Size</label>
          <input type="number" min="1" max="10000" id="batch_size_help_input" v-model="batch_size" :disabled="block_input" class="form-control" aria-describedby="batch_size_help">
          <div id="batch_size_help" class="form-text">How many samples the model gets when replaying</div>

          <label for="steps_input" class="form-label">Steps per game</label>
          <input type="number" min="1" max="10000" id="steps_input" v-model="steps" :disabled="block_input" class="form-control" aria-describedby="steps_input_help">
          <div id="steps_input_help" class="form-text">How many steps until the round is complete and the board is reset</div>
          <label for="training_rounds_input" class="form-label">Rounds per training</label>
          <input type="number" min="1" max="10000" id="training_rounds_input" v-model="training_rounds" :disabled="block_input" class="form-control" aria-describedby="training_rounds_input_help">
          <div id="training_rounds_input_help" class="form-text">How many rounds until the training is complete</div>
        </div>
        <div class="btn-group">
          <button type="button" class="btn btn-primary" :disabled="block_input || !model" @click="play">Play a round</button>
          <button type="button" class="btn btn-primary" :disabled="block_input" @click="new_model">New Model</button>
          <button type="button" class="btn btn-primary" :disabled="block_input || !currently_selected_model" @click="load_model" id="load_current_model">Load Model</button>
          <button type="button" class="btn btn-primary" :disabled="block_input || !model" @click="train">Train Model</button>
          <button type="button" class="btn btn-outline-danger" :disabled="!training" @click="stop_training">Stop Training</button>
          <button type="button" class="btn btn-primary" :disabled="block_input || !model" @click="save">Save current model</button>
          <button type="button" class="btn btn-warning" :disabled="block_input || !model" @click="overwrite">Overwrite current model</button>
          <button type="button" class="btn btn-danger" :disabled="block_input || !currently_selected_model" @click="delete_model">Delete current model</button>
        </div>
      </section>
    </div>
  </main>
  <div class="toast-container position-fixed top-0 end-0 p-3">
    <div id="liveToast" ref="liveToast" class="toast text-bg-danger" role="alert" aria-live="assertive" aria-atomic="true" data-bs-autohide="false">
      <div class="toast-header">
        <strong class="me-auto">Error</strong>
        <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
      <div class="toast-body">
        An error occurred. More details in the console.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watchEffect,
  Ref,
  onMounted,
  watch,
  toRaw,
  onBeforeMount,
  shallowRef,
  markRaw,
} from "vue";
import GameTable from "./components/game-table.vue";
import {Orchestrator} from "./Orchestrator";
import {Memory} from "./Memory";
import {Model} from "./Model";
import {GameEnv} from "./GameEnv";
import {io, loadLayersModel} from "@tensorflow/tfjs";
import {Toast} from "bootstrap";

const table_size = ref(6);
const max_score = ref(0);
const current_score = ref(0);
const models = ref({});
const game_table: Ref<typeof GameTable|null> = ref(null);
const liveToast: Ref<HTMLDivElement|null> = ref(null);
const currently_selected_model = ref("");
const model: Ref<Model|null> = shallowRef(null);
const memory_slots = ref(50000);
//@ts-ignore
const memory: Ref<Memory> = shallowRef(new Memory(memory_slots.value));
const orchestrator: Ref<Orchestrator|null> = ref(null);
const training_delay = ref(10);
const no_delay = ref(true);
const steps = ref(1000);
const play_steps = ref(0);
const training_rounds = ref(10);
const block_input = ref(false);
const training = ref(false);
const batch_size = ref(64);

const status = ref("idle");
const game_result = ref<"won" | "lost" | null>(null);

const MODEL_SAVE_PATH_ = 'indexeddb://game-model-'

function epsilonKey(path: string): string {
  return 'model-epsilon:' + path;
}

function saveEpsilon(path: string) {
  if (model.value) {
    localStorage.setItem(epsilonKey(path), String(model.value.getEpsilon()));
  }
}

function loadEpsilon(path: string): number {
  const stored = localStorage.getItem(epsilonKey(path));
  return stored !== null ? parseFloat(stored) : 1.0;
}


const game_env: Ref<GameEnv> = shallowRef(markRaw(new GameEnv(table_size.value)));

watch(table_size, (size) => {
  game_env.value = markRaw(new GameEnv(size));
});

function renderFromEnv(state: number[], score: number, won: boolean, lost: boolean) {
  game_table.value?.displayState(state, score, won, lost);
  current_score.value = score;
}

watch([model, memory, steps, training_delay, no_delay, game_env], () => {
  if (!model.value) {
    return
  }
  orchestrator.value = markRaw(new Orchestrator(game_env.value, model.value, memory.value, steps.value, training_delay.value, no_delay.value, renderFromEnv));
});

watch(memory_slots, () => {
  memory.value = markRaw(new Memory(memory_slots.value));
});

watchEffect(() => {
  if (current_score.value > max_score.value) {
    max_score.value = current_score.value;
  }
}, { flush: 'post' });

function sleep(time: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => resolve(), time)
  })
}

const ENCODING_CHANNELS = 12;

function stateSize(): number {
  return table_size.value * table_size.value * ENCODING_CHANNELS;
}

async function load_model() {
  model.value = markRaw(new Model({
    numStates: stateSize(),
    numActions: 4,
    batchSize: batch_size.value,
    network: await loadLayersModel(currently_selected_model.value),
    epsilon: loadEpsilon(currently_selected_model.value),
  }));
}

async function new_model() {
  model.value = markRaw(new Model({numStates: stateSize(), numActions: 4, batchSize: batch_size.value, boardSize: table_size.value}))
}

const global_lock = globalThis as { __2048_session_active?: boolean };

function sessionLocked(): boolean {
  return global_lock.__2048_session_active === true || training.value || block_input.value;
}

function lockSession() {
  global_lock.__2048_session_active = true;
}

function unlockSession() {
  global_lock.__2048_session_active = false;
}

async function play() {
  if (!orchestrator.value) {
    console.error("No orchestrator!");
    return;
  }
  if (sessionLocked()) {
    console.warn("Play blocked: another play/training session is already running");
    return;
  }
  lockSession();
  try {
    status.value = "Playing"
    game_result.value = null;
    block_input.value = true;
    game_env.value.reset();
    game_table.value?.displayState(game_env.value.state, game_env.value.score, false, false);
    current_score.value = 0;
    play_steps.value = 0;
    let needed_steps = 0;
    while (true) {
      const [lost, won] = await orchestrator.value.just_play();
      needed_steps++;
      play_steps.value = needed_steps;
      if (lost || won) {
        break
      }
      await sleep(training_delay.value)
    }
    status.value = "idle"
    block_input.value = false;
    console.log("Steps performed:", needed_steps);
  } finally {
    unlockSession();
  }
}

onBeforeMount(() => {
  const settings_string = localStorage.getItem("settings");
  if (settings_string) {
    const settings = JSON.parse(settings_string);
    memory_slots.value = settings["memory_slots"] === 5000 ? 50000 : (settings["memory_slots"] ?? 50000)
    steps.value = settings["steps"]
    training_rounds.value = settings["training_rounds"]
    no_delay.value = settings["no_delay"]
    table_size.value = settings["table_size"] ?? 6
    training_delay.value = settings["training_delay"]
    batch_size.value = settings["batch_size"] ?? 64
  }
  watch([memory_slots, steps, training_rounds, no_delay, table_size, training_delay, batch_size], ([memory_slots, steps, training_rounds, no_delay, table_size, training_delay, batch_size]) => {
    const save_obj = {
      memory_slots, steps, training_rounds, no_delay, table_size, training_delay, batch_size
    }
    localStorage.setItem("settings", JSON.stringify(save_obj))
  });
});

onMounted(async () => {
  models.value = await io.listModels();
});

function lost_fn() {
  game_result.value = "lost";
  console.log("You lost");
}

function won_fn() {
  game_result.value = "won";
  console.log("You won");
}

function clicked() {
  game_table.value?.reset()
}

async function delete_model() {
  await io.removeModel(currently_selected_model.value);
  localStorage.removeItem(epsilonKey(currently_selected_model.value));
  currently_selected_model.value = "";
  models.value = await io.listModels();
}

async function next_free_model_path(): Promise<string> {
  const existing = new Set(Object.keys(await io.listModels()));
  let i = 1;
  while (existing.has(MODEL_SAVE_PATH_ + i)) {
    i++;
  }
  return MODEL_SAVE_PATH_ + i;
}

async function save() {
  if (!model.value) {
    console.error("No model");
    return;
  }
  status.value = "Saving model";
  const path = await next_free_model_path();
  await toRaw(model.value).saveModel(path);
  saveEpsilon(path);
  status.value = `Saved model as ${path}`;
  models.value = await io.listModels();
}

async function overwrite() {
  if (!currently_selected_model.value) {
    console.error("No currently selected model");
    return
  }
  if (!model.value) {
    console.error("No model");
    return;
  }
  status.value = `Saving model as ${currently_selected_model.value}`;
  await toRaw(model.value).saveModel(currently_selected_model.value);
  saveEpsilon(currently_selected_model.value);
  status.value = `Saved model as ${currently_selected_model.value}`;
}

async function train() {
  if (!orchestrator.value) {
    console.error("No Orchestrator");
    return;
  }
  if (!model.value) {
    console.error("No model");
    return;
  }
  if (!game_table.value) {
    console.error("No game table")
    return;
  }
  if (sessionLocked()) {
    console.warn("Training blocked: another play/training session is already running");
    return;
  }
  lockSession();
  try {
    game_env.value.reset();
    game_table.value?.displayState(game_env.value.state, game_env.value.score, false, false);
    current_score.value = 0;
    game_result.value = null;
    block_input.value = true;
    training.value = true;
    await sleep(100);
    const training_start = performance.now();
    for (let i = 0; i < training_rounds.value; i++) {
      const round_start = performance.now();
      status.value = `Training round ${i+1}/${training_rounds.value}`;
      console.log(`Training round ${i+1}`);
      await orchestrator.value.run();
      const round_ms = performance.now() - round_start;
      const elapsed_ms = performance.now() - training_start;
      const avg_ms = elapsed_ms / (i + 1);
      console.log(`Training round ${i+1}/${training_rounds.value} took ${formatDuration(round_ms)} (avg ${formatDuration(avg_ms)}/round, total ${formatDuration(elapsed_ms)})`);
      status.value = `Training round ${i+1}/${training_rounds.value} done in ${formatDuration(round_ms)} (avg ${formatDuration(avg_ms)}/round)`;
      if (orchestrator.value.stopRequested) {
        break;
      }
      if (!no_delay.value) {
        await sleep(0);
      }
    }
    const total_training_ms = performance.now() - training_start;
    console.log(`Training finished ${training_rounds.value} round(s) in ${formatDuration(total_training_ms)}`);
    const interrupted = orchestrator.value.stopRequested;

    try {
      const target_path = currently_selected_model.value || autosave_path();
      status.value = `${interrupted ? "Training interrupted, a" : "A"}utosaving model to ${target_path}`;
      await toRaw(model.value).saveModel(target_path);
      saveEpsilon(target_path);
      if (!currently_selected_model.value) {
        currently_selected_model.value = target_path;
      }
      models.value = await io.listModels();
      status.value = `${interrupted ? "Training interrupted and autosaved" : "Trained and autosaved"} as ${target_path} (total ${formatDuration(total_training_ms)})`;
    } catch (e) {
      console.error("Autosave failed", e);
      status.value = `Training done in ${formatDuration(total_training_ms)} (${interrupted ? "interrupted" : "completed"}), autosave failed (see console)`;
    }
    training.value = false;
    block_input.value = false;
  } finally {
    unlockSession();
  }
}

function stop_training() {
  if (orchestrator.value) {
    orchestrator.value.requestStop();
    status.value = "Stopping training after current step...";
  }
}

function autosave_path(): string {
  return MODEL_SAVE_PATH_ + "autosave";
}

function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms.toFixed(0)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const restSeconds = seconds - minutes * 60;
  if (minutes < 60) {
    return `${minutes}m ${restSeconds.toFixed(0)}s`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes - hours * 60}m ${restSeconds.toFixed(0)}s`;
}

window.addEventListener('error', function(event) {
  console.error('Error captured:', event)
  const toastBootstrap = Toast.getOrCreateInstance(liveToast.value!);
  toastBootstrap.show();
  event.preventDefault();
});

window.addEventListener('unhandledrejection', function(event) {
  console.error('Error captured:', event)
  const toastBootstrap = Toast.getOrCreateInstance(liveToast.value!);
  toastBootstrap.show();
  event.preventDefault();
});

window.addEventListener('beforeunload', function(event) {
  if (training.value || block_input.value) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }
});
</script>
